import { createElement, useEffect, useRef, useState } from 'react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import dayjs from 'dayjs';
import noPublishedSchedule from '../../../../../assets/images/no_published_schedule.svg';
import { Badge } from 'primereact/badge';
import { capitalize } from 'lodash';
import {
    checkIfShiftAm,
    days,
    EmployeeScheduleData,
    getDateOfWeekday,
    getFirstThreeLetter,
    isDateInRange,
    ScheduleData,
    ScheduleMetadata,
    Staff,
} from '../../../../../helpers/venueScheduleHelper';
import CustomTooltip from './CustomTooltip';

interface RoleData {
    roleId: string;
    roleName: string;
}

interface GroupedDetails {
    roleData: RoleData;
    staffs: Staff[];
}

interface GroupedData {
    [id: string | number]: GroupedDetails;
}

// Interfaces and Function to group staff by assigned role
interface AssignedShifts {
    date: string;
    role: string;
    roleId?: string;
    shiftName?: string;
    startTime?: string;
    endTime?: string;
    isClose?: boolean;
    isBd?: boolean;
}

interface JobRole {
    id: string;
    jobRoleDescription: string;
    jobRoleName: string;
}

interface RowData {
    id: string;
    email: string | null;
    firstName: string;
    lastName: string;
    preferredName: string;
    jobRoles: JobRole[];
    assignedShifts: AssignedShifts[];
}

type Props = {
    staffsWithSchedule: ScheduleData | null;
    scheduleMetadata: ScheduleMetadata | null;
    selectedView: string | null;
    selectedWeek: dayjs.Dayjs;
    viewType: 'amPm' | 'normal';
};

const RoleBasedShiftScheduler = ({ staffsWithSchedule, scheduleMetadata, selectedWeek, selectedView, viewType }: Props) => {
    const dataTableHeaderRef = useRef<any>(null);
    const eventsTableRef = useRef<any>(null);
    const amPmTableRef = useRef<any>(null);

    const [groupedData, setGroupedData] = useState<GroupedData>({});
    const [allStaffsWithSchedule, setAllStaffsWithSchedule] = useState<Staff[] | undefined>([]);

    const checkHoliday = (day: string) => {
        if (staffsWithSchedule !== null) {
            const { holidays } = staffsWithSchedule;
            const date = getDateOfWeekday({ day, selectedWeek, format: 'YYYY-MM-DD' });
            const isHolidays =
                holidays.filter((holiday) => isDateInRange({ date: date, startDate: holiday.startDate, endDate: holiday.endDate })).length >
                0;
            return isHolidays;
        }
    };

    const checkMandatoryDays = (day: string) => {
        if (staffsWithSchedule !== null) {
            const { mandatoryDays } = staffsWithSchedule;
            const date = getDateOfWeekday({ day, selectedWeek, format: 'YYYY-MM-DD' });
            const isMandatory =
                mandatoryDays.filter((mandatoryDay) =>
                    isDateInRange({ date: date, startDate: mandatoryDay.startDate, endDate: mandatoryDay.endDate })
                ).length > 0;
            return isMandatory;
        }
    };

    const headerTemplate = ({ day }: { day: string; schedule: EmployeeScheduleData[]; customEvent: CustomEvent | undefined }) => {
        return (
            <div>
                <div className="mb-2 text-center">{getFirstThreeLetter(day)}</div>
                <div className="mb-2 text-center date-role-based">{getDateOfWeekday({ selectedWeek, day: day })}</div>

                {/* rendering holidays */}
                {checkHoliday(day) ? (
                    <div className={`${checkHoliday(day) && 'holiday-header'}`}>
                        <div className="mb-1 text-center text-nowrap fw-normal text-muted w-100">
                            <div className="d-flex align-center justify-content-center position-relative">
                                <div>Holiday</div>
                            </div>
                        </div>
                    </div>
                ) : (
                    ''
                )}

                {/* rendering mandatory days */}
                {checkMandatoryDays(day) ? (
                    <div className={`${checkMandatoryDays(day) && 'mandatory-day'}`}>
                        <div className="mb-1 text-center text-nowrap fw-normal text-muted w-100">
                            <div className="d-flex align-center justify-content-center position-relative">
                                <div>Mandatory Day</div>
                            </div>
                        </div>
                    </div>
                ) : (
                    ''
                )}
            </div>
        );
    };

    const staffNameTemplate = (rowData: RowData) => {
        const { preferredName, jobRoles } = rowData;
        const roles = jobRoles.map((role, idx) =>
            role?.jobRoleName === undefined ? '' : idx === jobRoles.length - 1 ? `${role?.jobRoleName}` : ` ${role?.jobRoleName} | `
        );
        const roleString = roles.join('').trim();

        return (
            <div className="text-nowrap" style={{ minWidth: '12rem', maxWidth: '12rem' }}>
                <div className="overflow-ellipsis fw-bold">{capitalize(preferredName ?? '')}</div>
                <div className="overflow-ellipsis" title={capitalize(roleString ?? '')}>
                    {roles}
                </div>
                <div className="overflow-ellipsis">{!roles.length ? 'No roles assigned' : ''}</div>
            </div>
        );
    };

    const cellBodyTemplate = ({ day, rowData }: { day: string; rowData: Staff }) => {
        const { shifts, preferredName } = rowData;
        const date = getDateOfWeekday({ day, selectedWeek, format: 'YYYY-MM-DD' });
        const shiftsArray = shifts[date];

        if (shiftsArray?.length) {
            // Sort the shiftsArray based on startTime and endTime
            const sortedShifts = shiftsArray.sort((a, b) => {
                const startA = dayjs(a.assignedShifts.startTime, 'hh:mm A');
                const startB = dayjs(b.assignedShifts.startTime, 'hh:mm A');
                const endA = dayjs(a.assignedShifts.endTime, 'hh:mm A');
                const endB = dayjs(b.assignedShifts.endTime, 'hh:mm A');

                // Compare start times first; if they're equal, compare end times
                if (startA.isSame(startB)) {
                    return endA.isBefore(endB) ? -1 : 1;
                }
                return startA.isBefore(startB) ? -1 : 1;
            });

            const shiftTemplate = createElement('span', { children: [<></>] });
            const shiftAMTemplate = createElement('div', {
                className: 'w-100 flex-grow-1 d-flex flex-column align-items-center',
                children: [<></>],
            });
            const shiftPMTemplate = createElement('div', {
                className: 'w-100 flex-grow-1 d-flex flex-column align-items-center',
                children: [<></>],
            });

            sortedShifts.forEach((shift, index) => {
                const { assignedShifts } = shift;
                const { startTime, endTime, isBd, isClose, role } = assignedShifts;

                const parsedDate = getDateOfWeekday({ selectedWeek, day, format: 'YYYY-MM-DD' });
                const isAmShift = checkIfShiftAm(startTime);

                const startTimeParsed = dayjs(startTime, 'hh:mm A');
                const endTimeParsed = dayjs(endTime, 'hh:mm A');

                const shiftDraggable = (
                    <CustomTooltip
                        key={`role-based-custom-tooltip-${rowData?.id}-${shift?.id}-${parsedDate}`}
                        staffName={preferredName}
                        showDelay={500}
                        content={sortedShifts}
                        showOnlyHovered={true}
                        currentShift={shift}
                    >
                        <div id={`custom-tooltip-${rowData?.id}-${shift?.id}-${parsedDate}`} className="scheduler-shift-card-body">
                            <div className="d-flex">
                                <div className="flex-grow-1 shift-name-main text-nowrap" role="button">
                                    {viewType === 'amPm' ? (
                                        <>
                                            {isClose
                                                ? `${
                                                      startTimeParsed.minute() === 0
                                                          ? startTimeParsed.format('h')
                                                          : startTimeParsed.format('h:m')
                                                  } - CL`
                                                : ''}
                                            {isBd
                                                ? `${
                                                      startTimeParsed.minute() === 0
                                                          ? startTimeParsed.format('h')
                                                          : startTimeParsed.format('h:m')
                                                  } - V`
                                                : ''}
                                            {!isBd && !isClose
                                                ? `${
                                                      startTimeParsed.minute() === 0
                                                          ? startTimeParsed.format('h')
                                                          : startTimeParsed.format('h:m')
                                                  } - ${
                                                      endTimeParsed.minute() === 0 ? endTimeParsed.format('h') : endTimeParsed.format('h:m')
                                                  }`
                                                : ''}

                                            {selectedView !== 'role-based' && (
                                                <span className="shift-name datatable-role-name">{role}</span>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {isClose ? `${startTime} - Close` : ''}
                                            {isBd ? `${startTime} - V` : ''}
                                            {!isBd && !isClose ? `${startTime} - ${endTime}` : ''}
                                            <span className="shift-name datatable-role-name">{role}</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CustomTooltip>
                );

                if (viewType === 'amPm') {
                    if (isAmShift) {
                        shiftAMTemplate.props.children.push(shiftDraggable);
                    } else {
                        shiftPMTemplate.props.children.push(shiftDraggable);
                    }
                } else {
                    shiftTemplate.props.children.push(shiftDraggable);
                }
            });

            return viewType === 'amPm' ? (
                <div className="d-flex gap-1">
                    {shiftAMTemplate}
                    {shiftPMTemplate}
                </div>
            ) : (
                shiftTemplate
            );

            // return shiftsArray.map((shift, index) => {
            //     const { assignedShifts } = shift
            //     const { startTime, endTime, isBd, isClose, role } = assignedShifts

            //     return (
            //         <CustomTooltip key={`role-based-shift-card-body-${shift?.id}`} staffName={preferredName} showDelay={500} content={shiftsArray}>
            //             <div className="scheduler-shift-card-body">
            //                 <div className="d-flex" style={{ minWidth: "12rem", maxWidth: "12rem" }}>
            //                     <div className="flex-grow-1 shift-name-main">
            //                         {isClose ? `${startTime} - Close` : ""}
            //                         {isBd ? `${startTime} - V` : ""}
            //                         {!isBd && !isClose ? `${startTime} - ${endTime}` : ''}
            //                         <span className="shift-name">{role}</span>
            //                     </div>
            //                 </div>
            //             </div>
            //         </CustomTooltip>
            //     )
            // })
        } else {
            return '';
        }
    };

    const renderColumns = () => {
        return days.map((day) => (
            <Column
                key={day}
                headerClassName="workTimeScheduleHeader"
                body={(data) => cellBodyTemplate({ day, rowData: data })}
                style={{ minWidth: '12rem', maxWidth: '12rem' }}
                className="shift-column"
            />
        ));
    };

    const renderHeader = () => {
        return days.map((day) => {
            const customEvent = scheduleMetadata?.customEvents?.find((event) => event.dayName.toLowerCase() === day.toLowerCase()) as
                | CustomEvent
                | undefined;
            return (
                <Column
                    key={day}
                    headerClassName="workTimeScheduleHeader"
                    header={(data) => headerTemplate({ day, schedule: data.props.value, customEvent })}
                    style={{ minWidth: '12rem', maxWidth: '12rem' }}
                    className="shift-column"
                />
            );
        });
    };

    const eventsTemplate = ({ day }: { day: string; schedule: EmployeeScheduleData[]; customEvent: CustomEvent | undefined }) => {
        const addedEvents = scheduleMetadata?.customEvents?.find((event) => event.dayName.toLowerCase() === day.toLowerCase());

        return (
            // rendering events
            <div className="d-flex flex-row align-items-center flex-wrap gap-1">
                {addedEvents?.eventData.map((event) => {
                    const currentEvent = event as any;
                    return (
                        <div key={event?.id} className="d-flex align-items-center px-2 reservation" style={{ minWidth: '80px' }}>
                            <small className="text-nowrap w-100 text-center">
                                {currentEvent?.guests} @ {dayjs(currentEvent?.time, 'HH:mm A').format('h:mm A')}
                            </small>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderEvents = () => {
        return days.map((day) => {
            const customEvent = scheduleMetadata?.customEvents?.find((event) => event.dayName.toLowerCase() === day.toLowerCase()) as
                | CustomEvent
                | undefined;
            return (
                <Column
                    key={day}
                    headerClassName="workTimeScheduleHeader"
                    header={(data) => eventsTemplate({ day, schedule: data.props.value, customEvent })}
                    style={{ minWidth: '12rem', maxWidth: '12rem' }}
                    className="shift-column"
                />
            );
        });
    };

    const getAmPmStaffCount = (day: string) => {
        const date = getDateOfWeekday({ day, selectedWeek, format: 'YYYY-MM-DD' });
        const filteredStaffs = staffsWithSchedule?.staffs.filter((staff) => Object.keys(staff.shifts).length > 0);
        const filteredStaffsByDay = filteredStaffs?.filter((staff) => staff.shifts[date]?.length > 0);

        let amCount = 0;
        let pmCount = 0;

        filteredStaffsByDay?.forEach((staff) => {
            const shifts = staff?.shifts[date];
            const amShifts = shifts?.filter((shift) => shift.assignedShifts.startTime.includes('AM'));
            const pmShifts = shifts?.filter((shift) => shift.assignedShifts.startTime.includes('PM'));
            amCount += amShifts?.length ? 1 : 0;
            pmCount += pmShifts?.length ? 1 : 0;
        });
        return { am: amCount, pm: pmCount };
    };

    const renderAmPm = () => {
        return days.map((day) => {
            const amPmCount = getAmPmStaffCount(day);
            return (
                <Column
                    key={day}
                    headerClassName="workTimeScheduleHeader"
                    header={(data) => {
                        return (
                            <div className="w-100 row" style={{ height: '30px' }}>
                                <div className="col-6 d-flex align-items-center justify-content-center">
                                    <span>AM</span>
                                    <Badge
                                        value={amPmCount?.am}
                                        style={{ fontSize: '0.65rem', minWidth: '1.05rem', height: '1rem', lineHeight: '1rem' }}
                                        className="ms-1"
                                    />
                                </div>
                                <div className="col-6 d-flex align-items-center justify-content-center">
                                    <span>PM</span>
                                    <Badge
                                        value={amPmCount?.pm}
                                        style={{ fontSize: '0.65rem', minWidth: '1.05rem', height: '1rem', lineHeight: '1rem' }}
                                        className="ms-1"
                                    />
                                </div>
                            </div>
                        );
                    }}
                    style={{ minWidth: '12rem', maxWidth: '12rem' }}
                    className="shift-column"
                />
            );
        });
    };

    useEffect(() => {
        if (dataTableHeaderRef?.current || eventsTableRef?.current || amPmTableRef?.current) {
            // Find the tbody element inside the DataTable
            const tbody = dataTableHeaderRef?.current?.querySelector('tbody');
            const tbodyEvents = eventsTableRef?.current?.querySelector('tbody');
            const tbodyAmPm = amPmTableRef?.current?.querySelector('tbody');
            if (tbody || tbodyEvents || tbodyAmPm) {
                // Remove tbody from the DOM
                tbody?.remove();
                tbodyEvents?.remove();
                tbodyAmPm?.remove();
            }
        }
    }, []); // Depend on `data` to rerun if `data` changes

    useEffect(() => {
        const updateScrollStyle = () => {
            const wrapper = document.querySelectorAll('.role-based-table>.p-datatable-wrapper');
            if (wrapper.length > 0) {
                wrapper.forEach((element) => {
                    (element as HTMLElement).style.overflow = 'visible';
                });
            }
        };
        setTimeout(() => {
            updateScrollStyle();
        }, 500);
    }, []);

    useEffect(() => {
        // get data of all the staffs assigned with shifts in the published schedule in role based view
        function groupByRoleAndDate(staffs: Staff[] | undefined): any {
            const groupedByRole: any = {};

            staffs?.forEach((staff: any) => {
                Object.values(staff.shifts).forEach((shiftArray: any) => {
                    shiftArray.forEach((shift: any) => {
                        const role = shift.assignedShifts.roleId;
                        if (!groupedByRole[role]) {
                            groupedByRole[role] = {
                                roleData: {
                                    roleId: shift.assignedShifts.roleId,
                                    roleName: shift.assignedShifts.role,
                                },
                                staffs: [],
                            };
                        }

                        // Check if this staff is already added under this role
                        let staffEntry = groupedByRole[role].staffs.find((entry: any) => entry.id === staff.id);
                        if (!staffEntry) {
                            staffEntry = {
                                id: staff.id,
                                email: staff.email,
                                firstName: staff.firstName,
                                lastName: staff.lastName,
                                preferredName: staff.preferredName,
                                jobRoles: staff.jobRoles.map((jobRole: any) => ({
                                    id: jobRole.id,
                                    jobRoleName: jobRole.jobRole?.jobRoleName,
                                    jobRoleDescription: jobRole.jobRole?.jobRoleDescription,
                                })),
                                shifts: {},
                            };
                            groupedByRole[role].staffs.push(staffEntry);
                        }

                        // Group shifts by date
                        const date = shift.scheduleDate;
                        if (!staffEntry.shifts[date]) {
                            staffEntry.shifts[date] = [];
                        }

                        staffEntry.shifts[date].push(shift);
                    });
                });
            });
            return groupedByRole;
        }
        const groupedStaffsData: GroupedData = groupByRoleAndDate(staffsWithSchedule?.staffs);
        setGroupedData(groupedStaffsData);

        // Get all the staffs with shifts assigned in published schedule for all staffs view
        // const filteredStaffs = staffsWithSchedule?.staffs.filter(staff => Object.keys(staff.shifts).length > 0);
        const filteredStaffs = staffsWithSchedule?.staffs.filter((staff) => Object.values(staff.shifts).flat().length > 0);
        setAllStaffsWithSchedule(filteredStaffs);
    }, [staffsWithSchedule, selectedWeek]);

    return (
        <div className="role-based-main-container border">
            {/* Rendering the header for data tables  */}
            <div className="role-based-header-container">
                <div ref={dataTableHeaderRef} className="role-based-header-container">
                    <DataTable
                        showGridlines
                        scrollable={false}
                        className=" role-based-table-header role-based-table w-100"
                        emptyMessage={' '}
                        pt={{
                            tbody: { style: { display: 'none' } },
                        }}
                    >
                        <Column
                            field="day"
                            className="overflow-ellipsis"
                            header={<div className="text-center" style={{ minWidth: '12rem', maxWidth: '12rem' }}></div>}
                        />
                        {renderHeader()}
                    </DataTable>
                </div>

                {/* table to show the events */}
                <div ref={eventsTableRef} className="role-based-header-container border-top">
                    <DataTable
                        showGridlines
                        scrollable={false}
                        className="role-based-table-header role-based-table w-100"
                        emptyMessage={' '}
                        pt={{
                            tbody: { style: { display: 'none' } },
                        }}
                    >
                        <Column
                            field="day"
                            className="overflow-ellipsis"
                            header={
                                <div className="text-center" style={{ minWidth: '12rem', maxWidth: '12rem' }}>
                                    Events
                                </div>
                            }
                        />
                        {renderEvents()}
                    </DataTable>
                </div>

                {/* table to show the events */}
                {viewType === 'amPm' && (
                    <div ref={amPmTableRef} className="role-based-header-container border-top">
                        <DataTable
                            showGridlines
                            scrollable={false}
                            className="role-based-table-header role-based-table w-100"
                            emptyMessage={' '}
                            pt={{
                                tbody: { style: { display: 'none' } },
                            }}
                        >
                            <Column
                                field="day"
                                className="overflow-ellipsis"
                                header={
                                    <div className="text-center" style={{ minWidth: '12rem', maxWidth: '12rem' }}>
                                        Shifts
                                    </div>
                                }
                            />
                            {renderAmPm()}
                        </DataTable>
                    </div>
                )}
            </div>

            {selectedView === 'role-based' ? (
                <>
                    {/* Rendering datatables with the grouped data */}
                    {Object.keys(groupedData)
                        .sort((a, b) => {
                            // sort the keys according to the role name to show role-based view in alphabetic order
                            const roleNameA = groupedData[a].roleData.roleName.toLowerCase();
                            const roleNameB = groupedData[b].roleData.roleName.toLowerCase();
                            if (roleNameA < roleNameB) return -1;
                            if (roleNameA > roleNameB) return 1;
                            return 0;
                        })
                        .map((key, index) => {
                            const { roleData, staffs } = groupedData[key];

                            return (
                                <div key={key} className="datatable-container" style={{ width: 'auto', display: 'grid' }}>
                                    <div className="role-based-table-title" style={{ width: 'auto' }}>
                                        {roleData.roleName}
                                    </div>
                                    <div>
                                        <DataTable
                                            value={staffs}
                                            showGridlines
                                            scrollable={false}
                                            className="role-based-table-list  role-based-table w-100"
                                            emptyMessage={' '}
                                        >
                                            <Column field="day" className="overflow-ellipsis" body={staffNameTemplate} />
                                            {renderColumns()}
                                        </DataTable>
                                    </div>
                                </div>
                            );
                        })}
                </>
            ) : (
                <div className="datatable-container" style={{ width: 'auto', display: 'grid' }}>
                    {/* table showing the staffs details */}
                    <div>
                        <DataTable
                            value={allStaffsWithSchedule}
                            showGridlines
                            stripedRows
                            scrollable={false}
                            className=" role-based-table-list role-based-table w-100"
                            emptyMessage={' '}
                        >
                            <Column field="day" className="overflow-ellipsis" body={staffNameTemplate} />
                            {renderColumns()}
                        </DataTable>
                    </div>
                </div>
            )}

            {!Object.keys(groupedData).length && (
                <div className="h-100 d-flex flex-column align-items-center justify-content-center">
                    <div className="flex-grow-1 align-items-center justify-content-center p-3 text-center mt-5">
                        <img src={noPublishedSchedule} alt="no published schedule" style={{ textAlign: 'center' }} />
                        <h5 className="mt-2 mb-3">No published schedule for the selected week</h5>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RoleBasedShiftScheduler;
